import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/auth.context';
import {
  onboardingApi,
  type SeminaryRequestRecord,
  type SeminaryRequestStatus,
} from '../../lib/api/onboarding.api';
import { formatDate } from '../../lib/i18n-format';

const STATUS_COLORS: Record<SeminaryRequestStatus, 'warning' | 'success' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'default',
};

/** `embedded` drops the outer padding + page title so this can render inside a
 *  tab of the platform console without duplicating the heading. */
export function SeminaryRequestsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation('onboarding');
  const { isPlatformAdmin, loading: authLoading } = useAuth();

  const [status, setStatus] = useState<SeminaryRequestStatus>('pending');
  const [requests, setRequests] = useState<SeminaryRequestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // approve dialog
  const [approveTarget, setApproveTarget] = useState<SeminaryRequestRecord | null>(null);
  const [finalSlug, setFinalSlug] = useState('');
  const [working, setWorking] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // reject dialog
  const [rejectTarget, setRejectTarget] = useState<SeminaryRequestRecord | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { requests: list } = await onboardingApi.list(status);
      setRequests(list);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (isPlatformAdmin) void load();
  }, [isPlatformAdmin, load]);

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (!isPlatformAdmin) {
    return (
      <Box p={4}>
        <Alert severity="warning">{t('admin.notAuthorized')}</Alert>
      </Box>
    );
  }

  async function confirmApprove() {
    if (!approveTarget) return;
    setWorking(true);
    setDialogError(null);
    try {
      await onboardingApi.approve({ requestId: approveTarget.id, finalSlug });
      setApproveTarget(null);
      await load();
    } catch {
      setDialogError(t('admin.approve.error'));
    } finally {
      setWorking(false);
    }
  }

  async function confirmReject() {
    if (!rejectTarget) return;
    setWorking(true);
    setDialogError(null);
    try {
      await onboardingApi.reject({ requestId: rejectTarget.id, reason });
      setRejectTarget(null);
      await load();
    } catch {
      setDialogError(t('admin.reject.error'));
    } finally {
      setWorking(false);
    }
  }

  return (
    <Box p={embedded ? 0 : { xs: 2, sm: 4 }}>
      {!embedded && (
        <>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            {t('admin.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t('admin.subtitle')}
          </Typography>
        </>
      )}

      <ToggleButtonGroup
        value={status}
        exclusive
        size="small"
        onChange={(_, v) => v && setStatus(v as SeminaryRequestStatus)}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="pending">{t('admin.filters.pending')}</ToggleButton>
        <ToggleButton value="approved">{t('admin.filters.approved')}</ToggleButton>
        <ToggleButton value="rejected">{t('admin.filters.rejected')}</ToggleButton>
      </ToggleButtonGroup>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error">{t('admin.loadError')}</Alert>
      ) : requests.length === 0 ? (
        <Typography color="text.secondary" py={4}>
          {t('admin.empty')}
        </Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.table.seminary')}</TableCell>
              <TableCell>{t('admin.table.contact')}</TableCell>
              <TableCell>{t('admin.table.slug')}</TableCell>
              <TableCell>{t('admin.table.created')}</TableCell>
              <TableCell>{t('admin.table.status')}</TableCell>
              <TableCell align="right">{t('admin.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.seminaryName}</TableCell>
                <TableCell>
                  <Stack>
                    <span>{r.contactName}</span>
                    <Typography variant="caption" color="text.secondary">
                      {r.contactEmail}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <code>{r.suggestedSlug}</code>
                </TableCell>
                <TableCell>{r.createdAt ? formatDate(r.createdAt) : '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLORS[r.status]}
                    label={t(`admin.status.${r.status}`)}
                  />
                </TableCell>
                <TableCell align="right">
                  {r.status === 'pending' && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setApproveTarget(r);
                          setFinalSlug(r.suggestedSlug);
                          setDialogError(null);
                        }}
                        sx={{ textTransform: 'none', background: '#0f766e' }}
                      >
                        {t('admin.approve.action')}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => {
                          setRejectTarget(r);
                          setReason('');
                          setDialogError(null);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        {t('admin.reject.action')}
                      </Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Approve dialog */}
      <Dialog
        open={!!approveTarget}
        onClose={() => !working && setApproveTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('admin.approve.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {approveTarget?.seminaryName}
          </Typography>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <TextField
            label={t('admin.approve.slugLabel')}
            helperText={t('admin.approve.slugHelp')}
            value={finalSlug}
            onChange={(e) => setFinalSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveTarget(null)}
            disabled={working}
            sx={{ textTransform: 'none' }}
          >
            {t('register.back')}
          </Button>
          <Button
            variant="contained"
            onClick={confirmApprove}
            disabled={working || !/^[a-z0-9-]+$/.test(finalSlug)}
            sx={{ textTransform: 'none', background: '#0f766e' }}
          >
            {working ? <CircularProgress size={20} color="inherit" /> : t('admin.approve.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog
        open={!!rejectTarget}
        onClose={() => !working && setRejectTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('admin.reject.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {rejectTarget?.seminaryName}
          </Typography>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <TextField
            label={t('admin.reject.reasonLabel')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectTarget(null)}
            disabled={working}
            sx={{ textTransform: 'none' }}
          >
            {t('register.back')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReject}
            disabled={working}
            sx={{ textTransform: 'none' }}
          >
            {working ? <CircularProgress size={20} color="inherit" /> : t('admin.reject.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
